<template lang="pug">
    div.banner(
        :style="{ backgroundImage: `url('${bannerImage}')` }"
    )

        like-component(v-if="showLikeButton && bannerSession" :session="bannerSession")

        div.imageOverlay(v-if="!bannerSession")

        // Edit button
        v-btn(
            v-if="isAdmin && isMindfulMoment && bannerSession && bannerSession.id"
            icon
            dark
            small
            @click="editItem"
            class="editItem"
        )
            v-icon(size="16") edit

        // Share button
        div.share(v-if="showShareButton")
            share-component(:item="shareContent" isMindfulMoment)

        // Copy link for mindful minute
        copy-link-component(v-if="isAdmin && isMindfulMoment" :link="url" )

        // Banner gradient background
        div.banneroverflow(v-if="bannerSession" class="justifyCenter")
            v-scale-transition
                div.bannerContent(:class="{centeredContent: !showPlayButton}")

                    div
                        // Play button on hover
                        div.bannerPlayButtonWrapper
                            v-btn.bannerPlayButton(
                                aria-label="play"
                                v-if="showPlayButton"
                                color="white"
                                flat
                                icon
                                @click="playAction"
                            )
                                v-icon.icon play_arrow

                        // Meditation title
                        div.meditationTitle(v-if="bannerSession.title && isMindfulMoment") {{ bannerSession.title }}
                        div.meditationTitle(v-if="bannerSession.title && !isMindfulMoment") {{ `${bannerSession.title} ${bannerSession.instructor ? 'with' : ''} ${bannerSession.instructor && bannerSession.instructor.fullName ? bannerSession.instructor.fullName : ''}` }}
                        div.imageAuthor(v-if="bannerSession.description && isMindfulMoment") {{ bannerSession.description }}

                        // Metditation started at
                        div.meditationDuration(v-if="bannerSession.startedAt && bannerSession.check_in_duration !== 0") {{ bannerSession | formattedDateTimeWithCheckIn }}
                        div.meditationDuration(v-else-if="bannerSession.startedAt") {{ bannerSession.startedAt | formattedDateAndTime }}
                    // Navigate to list
                    div.moreButton(v-if="moreButtonText && (bannerSession && bannerSession.id || !showPlayer)" @click="moreButtonAction")
                        v-icon.moreButtonIcon more_horiz
                        div {{ moreButtonText }}
</template>

<script>
    import Button from '../Button';
    import formattedDateAndTime from '~filters/FormattedDateAndTime';
    import formattedDateTimeWithCheckIn from '~filters/FormattedDateTimeWithCheckIn';
    import dayjs from 'dayjs';
    import VueTypes from 'vue-types';
    import { mapGetters } from 'vuex';
    import Share from '../Share';
    import CopyLink from '../CopyLink';
    import LikeMindfulMoment from '~components/LikeMindfulMoment';

    export default {
        components: {
            CopyLinkComponent: CopyLink,
            ButtonComponent: Button,
            ShareComponent: Share,
            LikeComponent: LikeMindfulMoment
        },
        filters: {
            formattedDateAndTime,
            formattedDateTimeWithCheckIn
        },
        props: {
            bannerSession: VueTypes.object.def(null),
            moreButtonText: VueTypes.string.def(''),
            isMindfulMoment: VueTypes.bool.def(false),
            moreButtonAction: VueTypes.func.def(() => {}),
            showPlayer: VueTypes.bool.def(false),
            showShareButton: VueTypes.bool.def(false),
            showLikeButton: VueTypes.bool.def(false)
        },
        data() {
            return {
                showJoinMeditation: false,
                timeInterval: null
            };
        },
        computed: {
            ...mapGetters('auth', ['isAdmin', 'user', 'isGoldman']),
            url() {
                return this.bannerSession
                    ? `${process.env.VUE_APP_HOST}/mindful-moments/view/${this.bannerSession.id}`
                    : '';
            },
            bannerImage() {
                if (this.bannerSession && this.bannerSession.poster) {
                    return this.bannerSession.poster;
                }
                return this.showPlayer
                    ? 'https://s3.us-east-2.amazonaws.com/chillanywhere.com/assests/themes/mindful-minutes-stub-min.png'
                    : 'https://s3.us-east-2.amazonaws.com/chillanywhere.com/assests/themes/blue_sky_1+(1)-min.jpg';
            },

            shareContent() {
                return {
                    title: this.bannerSession.title,
                    description: this.bannerSession.title,
                    emailSubject: `I want to share mindful moment with you`
                };
            },

            showPlayButton() {
                return (
                    !!this.showJoinMeditation &&
                    !!this.bannerSession.title &&
                    !this.showPlayer
                );
            }
        },
        watch: {
            bannerSession() {
                if (this.showPlayer) return;
                this.checkTime();
            }
        },
        created() {
            this.timeInterval = setInterval(this.checkTime, 10000);
            this.checkTime();
        },
        beforeDestroy() {
            clearInterval(this.timeInterval);
        },
        methods: {
            playAction() {
                if (this.bannerSession.type === 'recorded-live')
                    return this.openRecordedLive();
                return this.openMeditation();
            },

            /**
             * Navigate to meditation view
             */
            openMeditation() {
                this.$router.push({
                    name: 'dashboard.sessions.view',
                    params: { id: this.bannerSession.id }
                });
            },
            /**
             * Navigate to live recorded view
             */
            openRecordedLive() {
                this.$router
                    .push({
                        name: 'dashboard.sessions.view',
                        params: {
                            id: this.bannerSession.id
                        }
                    })
                    .catch(err => {});
            },

            /**
             * Play Video
             */
            editItem() {
                this.$router.push({
                    name: 'dashboard.editMindfulMinutes',
                    params: { id: this.bannerSession.id }
                });
            },

            /**
             * Check time period
             * @returns {boolean}
             */
            checkTime() {
                if (!this.bannerSession) return false;
                const currentDate = dayjs(new Date());
                const meditationDate = dayjs(this.bannerSession.startedAt);
                if (meditationDate.diff(currentDate, 'second') <= 300) {
                    return (this.showJoinMeditation = true);
                }

                return (this.showJoinMeditation = false);
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import "../../assets/style/_variables.styl"

    .imageOverlay
        width: 100%
        height: 100%
        background: linear-gradient(0deg, #022341 5.16%, rgba(2, 35, 65, 0.6) 100%)
        border-radius: 10px

    .banner
        border-radius: 10px
        background-size: cover
        text-align: center
        height: 323px
        transition: height 0.3s
        position: relative
        background-position: center

        >>> .video
            height: 323px

    .bannerPlayer
        width: 100%
        z-index: 3
        border-radius: 10px
        overflow: hidden

        .closeVideo
            position: absolute
            right: 15px
            top: 15px
            z-index: 1

    .bannerPlayButtonWrapper
        height: 30px 30px 20px

    .bannerPlayButton
        width: 40px
        height: 40px
        opacity: 1
        border-radius: 50%
        border: 1px solid rgba($theme.white, 0.9)

        .icon
            font-size: 30px

    .editItem
        position: absolute
        top: 15px
        right: 15px
        background: $theme.action
        border-radius: 50%


    .banneroverflow
        display: flex
        align-items: center
        justify-content: flex-end
        flex-direction: column
        height: 100%
        border-radius: 10px
        background: linear-gradient(0deg, rgba(2, 35, 65, 0.92) 0%, rgba(2, 35, 65, 0.55) 50%, rgba(2, 35, 65, 0.25) 100%)

        &.justifyCenter
            justify-content: center

        &.lightOverflow
            background: $gradient-light

    .bannerContent
        padding: 20px 25px

    .meditationTitle
    .meditationDuration
        font-size: 18px
        text-align: center
        font-weight: 300
        text-shadow: 0 1px 6px rgba(0, 0, 0, 0.7)

    .meditationTitle
        padding-top: 15px

    .moreButton
        margin-top: 30px
        display: inline-block
        color: $theme.white
        text-transform: uppercase
        cursor: pointer
        transition: opacity 0.3s, color 0.3s

        &:hover
            opacity: 0.75

            .moreButtonIcon
                color: $theme.primary

    .moreButtonIcon
        font-size: 40px
        height: 30px
        color: $theme.white

    .share
        position: absolute
        bottom: 6px
        right: 6px
        z-index: 2

    .imageAuthor
        position: absolute;
        bottom: 22px
        left: 15px
        opacity: 0.8
        font-size: 10px

    @media screen and (max-width: $grid-breakpoints.xl - 1)
        .banner
            height: 320px


    @media screen and (max-width: $grid-breakpoints.lg - 1)
        .banner
            height: 240px

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .banner
            height: 160px
        .moreButton
            margin-top: 8px
            font-size: 12px
            letter-spacing: 0.5px
        .moreButtonIcon
            font-size: 28px
            height: 20px
        .banneroverflow.justifyCenter
            justify-content: flex-start
            padding-top: 12px
        .bannerContent
            padding: 8px 16px
        .meditationTitle
            padding-top: 4px
        .meditationTitle
        .meditationDuration
            font-size: 16px
</style>
